import React, { useState, useEffect, useCallback } from 'react';
import { Save, Image as ImageIcon, X, Film, Plus, Trash2, Package } from 'lucide-react';
import FileUploader from './FileUploader';

// ============= PRODUCT IMAGES EDITOR (reorder / delete / add) =============
const ProductImagesEditor = ({ product, backendUrl, onClose, onUpdated }) => {
  const [images, setImages] = useState(product.images || []);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const move = (from, to) => {
    if (to < 0 || to >= images.length) return;
    const arr = [...images];
    [arr[from], arr[to]] = [arr[to], arr[from]];
    setImages(arr);
  };

  const remove = (i) => setImages(images.filter((_, idx) => idx !== i));
  const addImage = (url) => setImages([...images, url]);

  const save = async () => {
    setSaving(true);
    setMsg('');
    try {
      const res = await fetch(`${backendUrl}/api/admin/products/${product.id}/images`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(images),
      });
      if (res.ok) {
        setMsg('✓ Kaydedildi');
        onUpdated && onUpdated();
        setTimeout(onClose, 800);
      } else {
        setMsg('Hata: Kayıt başarısız');
      }
    } catch (e) {
      setMsg('Hata: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[400] bg-black/70 flex items-center justify-center p-4" data-testid="product-images-editor">
      <div className="bg-white rounded max-w-3xl w-full max-h-[85vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold">Görsel Düzenleme</h3>
            <p className="text-xs text-gray-500">{product.name} · {images.length} görsel</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-black">
            <X size={20} />
          </button>
        </div>

        {msg && (
          <div className={`mb-3 px-3 py-2 text-sm rounded ${msg.startsWith('✓') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>{msg}</div>
        )}

        <p className="text-xs text-gray-500 mb-3">↑/↓ ile sıralayın. İlk görsel ana görsel olur. × ile silin. Aşağıdan yeni görsel ekleyebilirsiniz.</p>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
          {images.map((img, i) => (
            <div key={i} className="relative aspect-square bg-gray-100 border overflow-hidden group">
              <img src={img} alt="" className="w-full h-full object-cover" />
              <div className="absolute top-1 left-1 bg-black/70 text-white text-[10px] px-1.5 py-0.5">
                {i + 1}{i === 0 ? ' · Ana' : ''}
              </div>
              <div className="absolute bottom-1 left-1 flex gap-1">
                <button
                  type="button"
                  onClick={() => move(i, i - 1)}
                  disabled={i === 0}
                  className="bg-black/70 text-white text-xs px-2 py-0.5 disabled:opacity-30 hover:bg-black"
                  data-testid={`editor-up-${i}`}
                >↑</button>
                <button
                  type="button"
                  onClick={() => move(i, i + 1)}
                  disabled={i === images.length - 1}
                  className="bg-black/70 text-white text-xs px-2 py-0.5 disabled:opacity-30 hover:bg-black"
                  data-testid={`editor-down-${i}`}
                >↓</button>
              </div>
              <button
                type="button"
                onClick={() => remove(i)}
                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                data-testid={`editor-remove-${i}`}
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>

        <FileUploader
          accept="image/*"
          label="+ Yeni Görsel Ekle"
          onUpload={addImage}
        />

        <div className="flex justify-end gap-2 mt-6 border-t pt-4">
          <button onClick={onClose} className="border px-4 py-2 text-sm hover:bg-gray-100">İptal</button>
          <button
            onClick={save}
            disabled={saving}
            className="bg-black text-white px-6 py-2 text-sm hover:bg-gray-800 disabled:opacity-50"
            data-testid="save-product-images"
          >
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
      </div>
    </div>
  );
};

const COLLECTIONS = [
  { slug: 'kokten-isiga', name: 'Kökten Işığa', year: '2025', accent: '#8B7355' },
  { slug: 'turk-motifleri', name: 'Türk Motifleri', year: '2026', accent: '#2E86AB' },
  { slug: 'yaz-masali', name: 'Yaz Masalı', year: '2026', accent: '#E4A853' },
  { slug: 'sakli-bahce', name: 'Saklı Bahçe', year: '2026', accent: '#4A7C59' },
  { slug: 'goc', name: 'Göç', year: '2026', accent: '#8B4A6B' },
  { slug: 'nazende', name: 'Nazende', year: '2026', accent: '#B08D57' },
];

const emptyAssets = { poster: '', hero_video: '', gallery: [], chapter_images: [] };

const CollectionManager = () => {
  const [selectedSlug, setSelectedSlug] = useState('kokten-isiga');
  const [assetsBySlug, setAssetsBySlug] = useState({});
  const [current, setCurrent] = useState(emptyAssets);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [customCollections, setCustomCollections] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [products, setProducts] = useState([]);
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingImagesProduct, setEditingImagesProduct] = useState(null);
  const emptyProduct = {
    name: '',
    material: '%100 İpek',
    dimensions: '65x65 cm',
    description: '',
    features: '',
    colors: '',
    sizes: '65x65',
    prices: '',
    images: [],
    isNew: true,
    isBestseller: false,
  };
  const [newProduct, setNewProduct] = useState(emptyProduct);
  const [savingProduct, setSavingProduct] = useState(false);
  const [newCol, setNewCol] = useState({
    slug: '',
    name: '',
    subtitle: '',
    year: '2026',
    accent: '#8B7355',
    dark: '#2E2419',
    number: '07',
    tagline: '',
    poster: '',
    available: true,
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchAll();
    fetchCustomCollections();
  }, []);

  useEffect(() => {
    setCurrent(assetsBySlug[selectedSlug] || emptyAssets);
    setMessage('');
    fetchProducts(selectedSlug);
    setShowProductForm(false);
    setNewProduct(emptyProduct);
    // eslint-disable-next-line
  }, [selectedSlug, assetsBySlug]);

  const fetchProducts = useCallback(async (slug) => {
    try {
      const res = await fetch(`${backendUrl}/api/products?collection=${slug}`);
      if (res.ok) {
        const data = await res.json();
        setProducts(data);
      }
    } catch (err) {
      console.error('Fetch products failed:', err);
    }
  }, [backendUrl]);

  const handleAddProduct = async () => {
    if (!newProduct.name) {
      setMessage('Ürün ismi zorunlu.');
      return;
    }
    setSavingProduct(true);
    setMessage('');
    try {
      const parsedPrices = newProduct.prices ? (() => {
        try { return JSON.parse(newProduct.prices); } catch { return null; }
      })() : null;

      const productData = {
        name: newProduct.name,
        altName: null,
        price: parsedPrices ? Object.values(parsedPrices)[0] : 0,
        prices: parsedPrices || undefined,
        material: newProduct.material,
        dimensions: newProduct.dimensions,
        description: newProduct.description,
        features: newProduct.features.split('\n').filter(f => f.trim()),
        colors: newProduct.colors.split(',').map(c => c.trim()).filter(c => c),
        sizes: newProduct.sizes.split(',').map(s => s.trim()).filter(s => s),
        collection: selectedSlug,
        images: newProduct.images,
        isNew: newProduct.isNew,
        isBestseller: newProduct.isBestseller,
      };

      const res = await fetch(`${backendUrl}/api/products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(productData),
      });

      if (res.ok) {
        setMessage(`✓ Ürün "${newProduct.name}" koleksiyona eklendi.`);
        setShowProductForm(false);
        setNewProduct(emptyProduct);
        fetchProducts(selectedSlug);
      } else {
        const err = await res.json();
        setMessage('Hata: ' + (err.detail || 'Ürün eklenemedi'));
      }
    } catch (err) {
      setMessage('Hata: ' + err.message);
    } finally {
      setSavingProduct(false);
    }
  };

  const handleDeleteProduct = async (id, name) => {
    if (!window.confirm(`"${name}" ürününü silmek istediğinize emin misiniz?`)) return;
    try {
      const res = await fetch(`${backendUrl}/api/products/${id}`, { method: 'DELETE' });
      if (res.ok) {
        setMessage('✓ Ürün silindi.');
        fetchProducts(selectedSlug);
      } else {
        setMessage('Hata: silinemedi');
      }
    } catch (err) {
      setMessage('Hata: ' + err.message);
    }
  };

  const fetchAll = async () => {
    try {
      const res = await fetch(`${backendUrl}/api/collections/assets`);
      if (res.ok) {
        const data = await res.json();
        setAssetsBySlug(data);
      }
    } catch (err) {
      console.error('Fetch collection assets failed:', err);
    }
  };

  const fetchCustomCollections = async () => {
    try {
      const res = await fetch(`${backendUrl}/api/collections/list`);
      if (res.ok) {
        const data = await res.json();
        setCustomCollections(data);
      }
    } catch (err) {
      console.error('Fetch custom collections failed:', err);
    }
  };

  const handleCreate = async () => {
    if (!newCol.slug || !newCol.name) {
      setMessage('Slug ve İsim zorunlu.');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${backendUrl}/api/admin/collections/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newCol,
          poster: newCol.poster || null,
          story: newCol.tagline ? [newCol.tagline] : [],
          products: [],
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setMessage(`✓ Yeni koleksiyon eklendi: ${data.name}. Sayfayı yenileyin (Ctrl+R).`);
        setShowCreate(false);
        setNewCol({ slug: '', name: '', subtitle: '', year: '2026', accent: '#8B7355', dark: '#2E2419', number: '07', tagline: '', poster: '', available: true });
        fetchCustomCollections();
      } else {
        setMessage('Hata: ' + (data.detail || 'Kayıt başarısız'));
      }
    } catch (err) {
      setMessage('Hata: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCustom = async (slug) => {
    if (!window.confirm(`"${slug}" koleksiyonunu silmek istediğinize emin misiniz?`)) return;
    try {
      await fetch(`${backendUrl}/api/admin/collections/${slug}`, { method: 'DELETE' });
      fetchCustomCollections();
      setMessage('✓ Koleksiyon silindi.');
    } catch (err) {
      setMessage('Hata: ' + err.message);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      const payload = {
        poster: current.poster || null,
        hero_video: current.hero_video || null,
        gallery: current.gallery || [],
        chapter_images: current.chapter_images || [],
      };
      const res = await fetch(`${backendUrl}/api/admin/collections/${selectedSlug}/assets`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setMessage('✓ Koleksiyon görselleri kaydedildi. Sayfayı yenileyin (Ctrl+R) ki değişiklikler görünsün.');
        fetchAll();
      } else {
        setMessage('Hata: Kayıt başarısız.');
      }
    } catch (err) {
      setMessage('Hata: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const removeChapterImage = (i) => {
    setCurrent({ ...current, chapter_images: current.chapter_images.filter((_, idx) => idx !== i) });
  };
  const removeGalleryImage = (i) => {
    setCurrent({ ...current, gallery: current.gallery.filter((_, idx) => idx !== i) });
  };

  const activeCol = COLLECTIONS.find((c) => c.slug === selectedSlug);

  return (
    <div className="p-6" data-testid="collection-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold">Koleksiyon Görsel Yönetimi</h2>
          <p className="text-sm text-gray-500 mt-1">
            Her koleksiyonun ana afiş (poster), hero videosu, galeri görselleri ve "Görsel Yolculuk" (chapter) görsellerini buradan yükleyebilirsiniz.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="bg-black text-white px-4 py-2 text-sm hover:bg-gray-800"
          data-testid="toggle-new-collection"
        >
          {showCreate ? 'İptal' : '+ Yeni Koleksiyon Ekle'}
        </button>
      </div>

      {/* Create new collection form */}
      {showCreate && (
        <div className="mb-6 border-2 border-black bg-gray-50 p-5 space-y-3" data-testid="new-collection-form">
          <h3 className="font-semibold">Yeni Koleksiyon</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs">Slug (URL, örn: yaz-masali) *</label>
              <input
                type="text"
                value={newCol.slug}
                onChange={(e) => setNewCol({ ...newCol, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                className="w-full border px-3 py-1.5 text-sm"
                placeholder="ornek-koleksiyon"
              />
            </div>
            <div>
              <label className="text-xs">İsim *</label>
              <input
                type="text"
                value={newCol.name}
                onChange={(e) => setNewCol({ ...newCol, name: e.target.value })}
                className="w-full border px-3 py-1.5 text-sm"
                placeholder="Örnek Koleksiyon"
              />
            </div>
            <div>
              <label className="text-xs">Alt Başlık</label>
              <input
                type="text"
                value={newCol.subtitle}
                onChange={(e) => setNewCol({ ...newCol, subtitle: e.target.value })}
                className="w-full border px-3 py-1.5 text-sm"
                placeholder="Kısa açıklama"
              />
            </div>
            <div>
              <label className="text-xs">Yıl</label>
              <input
                type="text"
                value={newCol.year}
                onChange={(e) => setNewCol({ ...newCol, year: e.target.value })}
                className="w-full border px-3 py-1.5 text-sm"
                placeholder="2026"
              />
            </div>
            <div>
              <label className="text-xs">Numara</label>
              <input
                type="text"
                value={newCol.number}
                onChange={(e) => setNewCol({ ...newCol, number: e.target.value })}
                className="w-full border px-3 py-1.5 text-sm"
                placeholder="07"
              />
            </div>
            <div>
              <label className="text-xs">Vurgu Rengi (accent)</label>
              <input
                type="color"
                value={newCol.accent}
                onChange={(e) => setNewCol({ ...newCol, accent: e.target.value })}
                className="w-full border h-8"
              />
            </div>
            <div>
              <label className="text-xs">Arka Plan Rengi (koyu)</label>
              <input
                type="color"
                value={newCol.dark}
                onChange={(e) => setNewCol({ ...newCol, dark: e.target.value })}
                className="w-full border h-8"
              />
            </div>
            <div className="col-span-2">
              <label className="text-xs">Tagline / Slogan</label>
              <input
                type="text"
                value={newCol.tagline}
                onChange={(e) => setNewCol({ ...newCol, tagline: e.target.value })}
                className="w-full border px-3 py-1.5 text-sm"
                placeholder="Bir iki kelimelik açılış cümlesi"
              />
            </div>
          </div>
          <button
            onClick={handleCreate}
            disabled={saving}
            className="bg-black text-white px-6 py-2 text-sm hover:bg-gray-800 disabled:opacity-50"
            data-testid="submit-new-collection"
          >
            {saving ? 'Ekleniyor...' : 'Koleksiyonu Ekle'}
          </button>
        </div>
      )}

      {/* List custom collections (deletable) */}
      {customCollections.length > 0 && (
        <div className="mb-6 border rounded p-4 bg-blue-50">
          <h4 className="text-sm font-semibold mb-2">Eklenen Yeni Koleksiyonlar</h4>
          <div className="flex flex-wrap gap-2">
            {customCollections.map((c) => (
              <div key={c.slug} className="inline-flex items-center gap-2 bg-white border px-3 py-1 text-xs">
                <span>{c.name} <span className="opacity-60">({c.slug} · {c.year})</span></span>
                <button
                  onClick={() => handleDeleteCustom(c.slug)}
                  className="text-red-500 hover:text-red-700"
                  data-testid={`delete-custom-${c.slug}`}
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {message && (
        <div className={`mb-4 px-4 py-3 rounded text-sm ${message.startsWith('✓') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
          {message}
        </div>
      )}

      {/* Collection tabs */}
      <div className="flex flex-wrap gap-2 mb-8 border-b pb-4">
        {[...COLLECTIONS, ...customCollections].map((c) => (
          <button
            key={c.slug}
            onClick={() => setSelectedSlug(c.slug)}
            className={`px-4 py-2 text-sm border transition-all ${
              selectedSlug === c.slug
                ? 'bg-black text-white border-black'
                : 'bg-white text-gray-700 border-gray-200 hover:border-black'
            }`}
            data-testid={`collection-tab-${c.slug}`}
          >
            <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ backgroundColor: c.accent }} />
            {c.name}
            <span className="text-xs opacity-60 ml-2">{c.year}</span>
          </button>
        ))}
      </div>

      {/* Editor */}
      <div className="space-y-8 max-w-4xl">
        <div>
          <h3 className="font-semibold text-lg mb-1">
            {activeCol?.name} <span className="text-xs opacity-60 font-normal">/{activeCol?.slug}</span>
          </h3>
          <p className="text-xs text-gray-500">
            Yüklediğiniz görseller Koleksiyonlar sayfasında bu koleksiyonun default görsellerinin yerine kullanılır.
          </p>
        </div>

        {/* Poster */}
        <div className="bg-gray-50 border rounded-lg p-5">
          <div className="flex items-center gap-2 mb-3">
            <ImageIcon size={18} />
            <h4 className="font-medium">Ana Afiş (Poster)</h4>
            <span className="text-xs text-gray-500">— Koleksiyon kartının büyük görseli. Dikey (3:4) tercih edilir.</span>
          </div>
          {current.poster ? (
            <div className="relative inline-block">
              <img src={current.poster} alt="Poster" className="max-h-64 rounded shadow" />
              <button
                onClick={() => setCurrent({ ...current, poster: '' })}
                className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                data-testid="remove-poster"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <FileUploader
              accept="image/*"
              label="Poster Görseli Yükle"
              onUpload={(url) => setCurrent({ ...current, poster: url })}
            />
          )}
        </div>

        {/* Hero Video */}
        <div className="bg-gray-50 border rounded-lg p-5">
          <div className="flex items-center gap-2 mb-3">
            <Film size={18} />
            <h4 className="font-medium">Hero Videosu (Opsiyonel)</h4>
            <span className="text-xs text-gray-500">— Koleksiyon detay modal'ının üst kısmında oynayacak video.</span>
          </div>
          {current.hero_video ? (
            <div className="relative inline-block">
              <video src={current.hero_video} className="max-h-48 rounded shadow" controls muted />
              <button
                onClick={() => setCurrent({ ...current, hero_video: '' })}
                className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                data-testid="remove-hero-video"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <FileUploader
              accept="video/*"
              label="Video Yükle"
              onUpload={(url) => setCurrent({ ...current, hero_video: url })}
            />
          )}
        </div>

        {/* Chapter images */}
        <div className="bg-gray-50 border rounded-lg p-5">
          <div className="flex items-center gap-2 mb-3">
            <ImageIcon size={18} />
            <h4 className="font-medium">Görsel Yolculuk (Chapters)</h4>
            <span className="text-xs text-gray-500">
              — Detay modal'ındaki 3 kartın görselleri (sinematik viewer'da açılır). Sıralı ekleyin.
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
            {(current.chapter_images || []).map((img, i) => (
              <div key={i} className="relative aspect-[3/4] bg-white border rounded overflow-hidden">
                <img src={img} alt="" className="w-full h-full object-cover" />
                <div className="absolute top-1 left-1 bg-black/60 text-white text-xs px-1.5 py-0.5">
                  {String(i + 1).padStart(2, '0')}
                </div>
                <button
                  onClick={() => removeChapterImage(i)}
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  data-testid={`remove-chapter-${i}`}
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
          <FileUploader
            accept="image/*"
            label="Chapter Görseli Ekle"
            onUpload={(url) =>
              setCurrent({ ...current, chapter_images: [...(current.chapter_images || []), url] })
            }
          />
        </div>

        {/* Gallery */}
        <div className="bg-gray-50 border rounded-lg p-5">
          <div className="flex items-center gap-2 mb-3">
            <ImageIcon size={18} />
            <h4 className="font-medium">Galeri Görselleri</h4>
            <span className="text-xs text-gray-500">— Modal'ın altındaki galeride sergilenecek görseller.</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
            {(current.gallery || []).map((img, i) => (
              <div key={i} className="relative aspect-video bg-white border rounded overflow-hidden">
                <img src={img} alt="" className="w-full h-full object-cover" />
                <button
                  onClick={() => removeGalleryImage(i)}
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  data-testid={`remove-gallery-${i}`}
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
          <FileUploader
            accept="image/*"
            label="Galeri Görseli Ekle"
            onUpload={(url) => setCurrent({ ...current, gallery: [...(current.gallery || []), url] })}
          />
        </div>

        {/* Products section - INLINE product management */}
        <div className="bg-amber-50/60 border-2 border-amber-200 rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Package size={20} />
              <h4 className="font-semibold text-lg">Bu Koleksiyonun Ürünleri</h4>
              <span className="text-xs bg-black text-white px-2 py-0.5 rounded-full">
                {products.length}
              </span>
            </div>
            <button
              onClick={() => setShowProductForm(!showProductForm)}
              className="inline-flex items-center gap-2 bg-black text-white px-4 py-2 text-sm hover:bg-gray-800"
              data-testid="toggle-add-product"
            >
              {showProductForm ? (
                <><X size={14} /> İptal</>
              ) : (
                <><Plus size={14} /> Yeni Ürün Ekle</>
              )}
            </button>
          </div>

          {/* Product creation form (inline, all in one place) */}
          {showProductForm && (
            <div className="bg-white border p-5 mb-4 space-y-4" data-testid="inline-product-form">
              <h5 className="font-semibold">Yeni Ürün — {activeCol?.name || selectedSlug}</h5>

              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="text-xs font-medium">Ürün İsmi *</label>
                  <input
                    type="text"
                    value={newProduct.name}
                    onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                    className="w-full border px-3 py-2 text-sm"
                    placeholder="Örn: Yıldız Sarayı"
                    data-testid="inline-product-name"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium">Kumaş / Malzeme</label>
                  <input
                    type="text"
                    value={newProduct.material}
                    onChange={(e) => setNewProduct({ ...newProduct, material: e.target.value })}
                    className="w-full border px-3 py-2 text-sm"
                    placeholder="%100 Has İpek"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium">Boyutlar (virgülle)</label>
                  <input
                    type="text"
                    value={newProduct.sizes}
                    onChange={(e) => setNewProduct({ ...newProduct, sizes: e.target.value })}
                    className="w-full border px-3 py-2 text-sm"
                    placeholder="65x65, 90x90"
                  />
                </div>

                <div className="col-span-2">
                  <label className="text-xs font-medium">Fiyatlar (JSON, opsiyonel)</label>
                  <input
                    type="text"
                    value={newProduct.prices}
                    onChange={(e) => setNewProduct({ ...newProduct, prices: e.target.value })}
                    className="w-full border px-3 py-2 text-sm font-mono"
                    placeholder='{"65x65": 2600, "90x90": 3898}'
                  />
                </div>

                <div>
                  <label className="text-xs font-medium">Renkler (virgülle)</label>
                  <input
                    type="text"
                    value={newProduct.colors}
                    onChange={(e) => setNewProduct({ ...newProduct, colors: e.target.value })}
                    className="w-full border px-3 py-2 text-sm"
                    placeholder="Mavi, Bordo"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium">Boyut Etiketi</label>
                  <input
                    type="text"
                    value={newProduct.dimensions}
                    onChange={(e) => setNewProduct({ ...newProduct, dimensions: e.target.value })}
                    className="w-full border px-3 py-2 text-sm"
                    placeholder="65x65 cm"
                  />
                </div>

                <div className="col-span-2">
                  <label className="text-xs font-medium">Açıklama</label>
                  <textarea
                    value={newProduct.description}
                    onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                    rows={2}
                    className="w-full border px-3 py-2 text-sm"
                  />
                </div>

                <div className="col-span-2">
                  <label className="text-xs font-medium">Özellikler (her satırda bir özellik)</label>
                  <textarea
                    value={newProduct.features}
                    onChange={(e) => setNewProduct({ ...newProduct, features: e.target.value })}
                    rows={3}
                    className="w-full border px-3 py-2 text-sm"
                    placeholder="%100 Has İpek&#10;El baskısı&#10;Twill dokusu"
                  />
                </div>

                <div className="col-span-2">
                  <label className="text-xs font-medium block mb-2">Ürün Görselleri (birden fazla)</label>
                  <p className="text-[10px] text-gray-500 mb-2">İlk görsel ana görsel olur. ↑/↓ ile sırayı değiştirebilirsiniz.</p>
                  <div className="grid grid-cols-4 gap-2 mb-2">
                    {newProduct.images.map((img, i) => (
                      <div key={i} className="relative aspect-square bg-gray-100 border">
                        <img src={img} alt="" className="w-full h-full object-cover" />
                        {/* Position badge */}
                        <div className="absolute top-1 left-1 bg-black/70 text-white text-[9px] px-1.5 py-0.5">
                          {i + 1}{i === 0 ? ' · Ana' : ''}
                        </div>
                        {/* Reorder buttons */}
                        <div className="absolute bottom-1 left-1 flex gap-0.5">
                          <button
                            type="button"
                            onClick={() => {
                              if (i === 0) return;
                              const arr = [...newProduct.images];
                              [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
                              setNewProduct({ ...newProduct, images: arr });
                            }}
                            disabled={i === 0}
                            className="bg-black/70 text-white text-[10px] px-1.5 py-0.5 hover:bg-black disabled:opacity-30"
                            data-testid={`move-image-up-${i}`}
                            aria-label="Yukarı taşı"
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (i === newProduct.images.length - 1) return;
                              const arr = [...newProduct.images];
                              [arr[i], arr[i + 1]] = [arr[i + 1], arr[i]];
                              setNewProduct({ ...newProduct, images: arr });
                            }}
                            disabled={i === newProduct.images.length - 1}
                            className="bg-black/70 text-white text-[10px] px-1.5 py-0.5 hover:bg-black disabled:opacity-30"
                            data-testid={`move-image-down-${i}`}
                            aria-label="Aşağı taşı"
                          >
                            ↓
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => setNewProduct({ ...newProduct, images: newProduct.images.filter((_, idx) => idx !== i) })}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5"
                          data-testid={`remove-inline-image-${i}`}
                          aria-label="Görseli sil"
                        >
                          <X size={10} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <FileUploader
                    accept="image/*"
                    label="Ürün Görseli Ekle"
                    onUpload={(url) => setNewProduct({ ...newProduct, images: [...newProduct.images, url] })}
                  />
                </div>

                <div className="col-span-2 flex items-center gap-4">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={newProduct.isNew}
                      onChange={(e) => setNewProduct({ ...newProduct, isNew: e.target.checked })}
                    />
                    Yeni ürün etiketi
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={newProduct.isBestseller}
                      onChange={(e) => setNewProduct({ ...newProduct, isBestseller: e.target.checked })}
                    />
                    Çok satan
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  onClick={() => { setShowProductForm(false); setNewProduct(emptyProduct); }}
                  className="border px-4 py-2 text-sm hover:bg-gray-100"
                >
                  İptal
                </button>
                <button
                  onClick={handleAddProduct}
                  disabled={savingProduct}
                  className="bg-black text-white px-6 py-2 text-sm hover:bg-gray-800 disabled:opacity-50"
                  data-testid="submit-inline-product"
                >
                  {savingProduct ? 'Ekleniyor...' : 'Koleksiyona Ekle'}
                </button>
              </div>
            </div>
          )}

          {/* Product list */}
          {products.length === 0 ? (
            <p className="text-sm text-gray-500 italic py-6 text-center">
              Bu koleksiyonda henüz ürün yok. Yukarıdaki "Yeni Ürün Ekle" butonundan başlayın.
            </p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {products.map((p) => (
                <div key={p.id} className="bg-white border rounded p-2 group" data-testid={`inline-product-${p.id}`}>
                  <div className="aspect-square bg-gray-100 mb-2 overflow-hidden relative">
                    {p.images && p.images[0] ? (
                      <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-300">
                        <ImageIcon size={30} />
                      </div>
                    )}
                    <div className="absolute inset-x-0 bottom-0 p-1 bg-gradient-to-t from-black/80 to-transparent flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => setEditingImagesProduct(p)}
                        className="flex-1 bg-white/90 text-black text-[10px] py-1 hover:bg-white"
                        data-testid={`edit-images-${p.id}`}
                      >
                        Görselleri Düzenle ({(p.images || []).length})
                      </button>
                    </div>
                    <button
                      onClick={() => handleDeleteProduct(p.id, p.name)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`delete-inline-product-${p.id}`}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                  <div className="text-xs font-medium truncate">{p.name}</div>
                  <div className="text-[10px] text-gray-500">
                    {p.sizes?.join(' · ') || '—'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Image reorder/edit modal for existing products */}
        {editingImagesProduct && (
          <ProductImagesEditor
            product={editingImagesProduct}
            backendUrl={backendUrl}
            onClose={() => setEditingImagesProduct(null)}
            onUpdated={() => {
              fetchProducts(selectedSlug);
              // refresh the editingImages product from products state after fetch
            }}
          />
        )}

        {/* Save */}
        <div className="sticky bottom-0 bg-white border-t pt-4 pb-2 flex items-center justify-end gap-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-black text-white px-6 py-3 hover:bg-gray-800 disabled:opacity-50"
            data-testid="save-collection-assets"
          >
            <Save size={16} />
            {saving ? 'Kaydediliyor...' : 'Koleksiyonu Kaydet'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CollectionManager;
